import { ApiError } from '@trackizy/types';

export interface TokenStorage {
  get(): Promise<string | null> | string | null;
  set(token: string | null, opts?: { remember?: boolean }): Promise<void> | void;
}

export interface ClientConfig {
  baseUrl: string;
  storage: TokenStorage;
  fetchImpl?: typeof fetch;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: string | FormData | URLSearchParams | null;
  /** Deadline for the whole request — connect AND body read. Defaults to
   *  15 s. Ignored when the caller supplies its own `signal` (the caller
   *  owns cancellation then). Uploads go through raw() and are never
   *  bounded here. */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Unauthenticated sign-in endpoints: a 401 here means "wrong credentials /
 * wrong code", never "your session is dead", so it must not clear a token
 * that is already in storage (Staff audit SA8 — a wrong OTP typed while a
 * worker was signed in used to sign them out).
 */
const SIGN_IN_PATHS = new Set(['/auth/login', '/auth/staff/request-otp', '/auth/staff/verify-otp']);

export function isSignInPath(path: string): boolean {
  return SIGN_IN_PATHS.has(path);
}

export class InternalRequest {
  constructor(private readonly config: ClientConfig) {}

  /** Swap the API host at runtime (multi-server / per-user discovery). Every
   *  subsequent request reads config.baseUrl, so this takes effect immediately. */
  setBaseUrl(url: string): void {
    this.config.baseUrl = url;
  }

  getBaseUrl(): string {
    return this.config.baseUrl;
  }

  async request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T | null> {
    const { timeoutMs = DEFAULT_TIMEOUT_MS, ...init } = options;
    const token = await Promise.resolve(this.config.storage.get());
    const headers: Record<string, string> = {
      ...(init.body && typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers as Record<string, string> | undefined),
    };
    const fetchImpl = this.config.fetchImpl ?? globalThis.fetch;
    const url = `${this.config.baseUrl}${path}`;

    // Caller-supplied signal → the caller owns cancellation; no default deadline.
    if (init.signal) {
      const res = await fetchImpl(url, { ...init, headers } as RequestInit);
      return this.consumeResponse<T>(res, path);
    }

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new ApiError(0, 'timeout', 'Request timed out'));
      }, timeoutMs);
    });
    try {
      // Race the deadline against BOTH the fetch and the body read: a
      // connection that dies mid-body still rejects on time, and a fetch
      // impl that ignores abort signals still can't hang the caller.
      const res = await Promise.race([
        fetchImpl(url, { ...init, headers, signal: controller.signal } as RequestInit),
        deadline,
      ]);
      return await Promise.race([this.consumeResponse<T>(res, path), deadline]);
    } finally {
      clearTimeout(timer);
    }
  }

  private async consumeResponse<T>(res: Response, path: string): Promise<T | null> {
    if (!res.ok) {
      let payload: { error?: string; code?: string; message?: string; details?: unknown } = {};
      let parsed = false;
      try {
        payload = (await res.json()) as typeof payload;
        parsed = true;
      } catch {
        /* non-JSON error body */
      }
      if (res.status === 401 && !isSignInPath(path)) {
        await Promise.resolve(this.config.storage.set(null));
      }
      const msg = payload.message ?? payload.error ?? res.statusText;
      throw new ApiError(res.status, payload.code ?? null, msg, payload.details, parsed ? payload : undefined);
    }
    if (res.status === 204) return null;
    return (await res.json()) as T;
  }

  /** Raw fetch pass-through (for FormData uploads that can't use the JSON helper). */
  async raw(path: string, init: RequestInit): Promise<Response> {
    const token = await Promise.resolve(this.config.storage.get());
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers as Record<string, string> | undefined),
    };
    const fetchImpl = this.config.fetchImpl ?? globalThis.fetch;
    return fetchImpl(`${this.config.baseUrl}${path}`, { ...init, headers });
  }

  getStorage(): TokenStorage {
    return this.config.storage;
  }
}
